import { getDimensionPrice } from '../../../components/PrivateOfferForm/DetailedOffer/utils'
import { useFlyOutProductsBasedOnSelectedCloud } from './useFlyOutProductsBasedOnSelectedCloud'
import { ResaleAuthorizationFields } from '../../../modules/resaleAuthorization/types'
import {
  PrivateOfferFields,
  OffersWithErrors,
} from '../../../modules/privateOffer/privateOfferCreation/reducer'
import {
  arrayFieldsForValidations,
  getArrayFieldsForValidation,
} from '../../../modules/privateOffer/privateOfferCreation/utils/helperFunctions'
import { get, isEmpty, isNil } from 'lodash'
import { PlanType } from '@labrav/react-components'
import {
  AzureOfferPlan,
  AzurePricingType,
  Dimension,
  EulaType,
  Installments,
  OfferRecipients,
  PriceTypes,
  RecurrentPriceModeType,
} from '../../../modules/privateOffer/reducer'
import { useSelector } from '../../../store/hooks'
import { useEffect, useMemo, useState } from 'react'
import { ResellerInfo } from '../../../admin/modules/resaleAuthorizations/reducer'
import { planTypesForCommonFields } from '../constants'
import { useCurrentCloudProvider, isNonEmptyNumber } from '@labrav/common'
import {
  CloudMarketplace,
  Product,
} from '../../../modules/productsListing/reducer'

export interface isErrorInOfferArrayFieldsArgsType {
  emptyValueFieldsToValidate: string[]
  offerObject: Record<string, unknown>
  errors: Record<string, unknown>
  flexiblePaymentTerms: boolean
  planType: PlanType
  isTypeCPPO?: boolean
  pricesArray?: [keyof PriceTypes]
}

export const isErrorInOfferArrayFields = ({
  emptyValueFieldsToValidate,
  offerObject,
  errors,
  flexiblePaymentTerms,
  planType,
  isTypeCPPO = false,
  pricesArray = ['price'],
}: isErrorInOfferArrayFieldsArgsType) => {
  const { azurePricingType } = offerObject
  return emptyValueFieldsToValidate.some(key => {
    const data = offerObject[key] as Array<Record<string, unknown>>
    return isValidOffersArrayField({
      key,
      data,
      errors,
      flexiblePaymentTerms,
      planType,
      isTypeCPPO,
      pricesArray,
      azurePricingType: azurePricingType
        ? (azurePricingType as AzurePricingType)
        : undefined,
    })
  })
}

const commonSingleValueFields = [
  'privateOfferName',
  'eulaType',
  'companyName',
  'emailCustomMessage',
]
const singleValueFieldsAWS = [...commonSingleValueFields, 'awsAccountId']
const singleValueFieldsAzure = [
  ...commonSingleValueFields,
  'billingAccountId',
  'offerExpirationDate',
  'acceptBy',
  'preparedBy',
]

const returnSingleValueFieldBasedOnCloud = (cloud: CloudMarketplace) => {
  switch (cloud) {
    case 'AWS': {
      return singleValueFieldsAWS
    }
    case 'AZURE': {
      return singleValueFieldsAzure
    }
    case 'GCP': {
      return commonSingleValueFields
    }
    case 'REDHAT': {
      return commonSingleValueFields
    }
  }
}
export const usePrivateofferSendAndDraftDisabled = (
  productId: string,
  eulaFiles: File[]
) => {
  const offer = useSelector<OffersWithErrors | undefined>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[productId]
  )

  const privateOfferWithErrors = useMemo(() => {
    return offer || ({} as OffersWithErrors)
  }, [offer])
  const { errors, ...remainingOffer } = privateOfferWithErrors
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const selectedCloudMarketplace = useCurrentCloudProvider()
  const planType =
    products.find((product: Product) => product.productId === productId)
      ?.productType || PlanType.SAAS_CONTRACT
  const privateOffer = remainingOffer as unknown as Record<string, unknown>
  const [sendDisabled, setSendDisabled] = useState<boolean>(true)
  const [draftDisabled, setDraftDisabled] = useState<boolean>(true)
  useEffect(() => {
    if (!isEmpty(privateOffer)) {
      const emptyValueFieldsToValidate = [
        ...getArrayFieldsForValidation(selectedCloudMarketplace),
      ]
      const metaDataIndex = arrayFieldsForValidations.indexOf('metaData')
      emptyValueFieldsToValidate.splice(metaDataIndex, 1)

      const isErrorInPrivateOffersArrayFields = isErrorInOfferArrayFields({
        emptyValueFieldsToValidate,
        offerObject: privateOffer,
        errors,
        flexiblePaymentTerms: privateOffer.flexiblePaymentTerms as boolean,
        planType,
      })
      const renewalCheck = !!(
        privateOffer?.renewal && isNil(privateOffer?.renewalChannel)
      )
      const shouldCheckStartDate = privateOffer?.variableStartDate === false

      const singleValueFieldKey = [
        ...returnSingleValueFieldBasedOnCloud(selectedCloudMarketplace),
        ...(shouldCheckStartDate && selectedCloudMarketplace === 'AZURE'
          ? ['offerStartDate']
          : []),
        ...(shouldCheckStartDate &&
        selectedCloudMarketplace === 'AWS' &&
        planType === PlanType.SAAS_CONTRACT
          ? ['agreementStartDate']
          : []),
      ]
      const fieldValueCheck = singleValueFieldKey.some(
        field => isEmpty(privateOffer[field]) || !isEmpty(errors[field])
      )
      const singleFieldErrorCheck = singleValueFieldKey.some(
        field => !isEmpty(errors[field])
      )
      const eulaFileErrCheck = !isEmpty(errors.eulaFile)
      const eulaFilesCheck =
        privateOffer.eulaType === EulaType.CUSTOM &&
        isEmpty(eulaFiles) &&
        (isEmpty(privateOffer.eulaFile) || privateOffer.eulaFile === null)
      const subscriptionEndDateCheckError = !isEmpty(errors.subscriptionEndDate)
      const subscriptionEndDateCheck =
        planType === PlanType.SAAS_SUBSCRIPTION &&
        (isEmpty(privateOffer.subscriptionEndDate) ||
          subscriptionEndDateCheckError ||
          isEmpty(privateOffer.offerExpirationDate))
      const serviceLengthCheckError =
        isNaN(privateOffer.serviceLength as unknown as number) ||
        !isEmpty(errors.serviceLength)
      const contractMiscellaneousCheck =
        planTypesForCommonFields.includes(planType) &&
        (isEmpty(privateOffer.offerExpirationDate) ||
          privateOffer.serviceLength == null ||
          serviceLengthCheckError)
      const metaDataCheck =
        !isEmpty(privateOffer.metaData) &&
        validateMetaData(
          privateOffer.metaData as Array<Record<string, unknown>>,
          errors.metaData as Array<Record<string, unknown>>
        )
      const commonDraftCheck =
        isErrorInPrivateOffersArrayFields ||
        singleFieldErrorCheck ||
        eulaFileErrCheck
      const checkVariableStartDate =
        (selectedCloudMarketplace === 'AWS' &&
          planType === PlanType.SAAS_CONTRACT) ||
        selectedCloudMarketplace === 'AZURE'
          ? isNil(privateOffer?.variableStartDate)
          : false
      const commonSendCheck =
        isErrorInPrivateOffersArrayFields ||
        fieldValueCheck ||
        metaDataCheck ||
        eulaFileErrCheck ||
        checkVariableStartDate
      if (selectedCloudMarketplace === 'AWS') {
        const emptyRowCheck = checkEmptyRows(
          privateOffer?.installments as Installments[],
          privateOffer?.offerRecipients as OfferRecipients[],
          privateOffer?.flexiblePaymentTerms as boolean,
          privateOffer?.dimensions as Dimension[]
        )

        setSendDisabled(
          emptyRowCheck ||
            commonSendCheck ||
            eulaFilesCheck ||
            subscriptionEndDateCheck ||
            contractMiscellaneousCheck ||
            renewalCheck
        )
        setDraftDisabled(
          commonDraftCheck ||
            subscriptionEndDateCheckError ||
            serviceLengthCheckError
        )
      } else if (selectedCloudMarketplace === 'AZURE') {
        setSendDisabled(
          isEmpty(privateOffer?.offerRecipients as OfferRecipients[]) ||
            !(privateOffer?.plans as AzureOfferPlan[]).length ||
            commonSendCheck ||
            isEmpty(privateOffer?.azurePricingType)
        )
        setDraftDisabled(commonDraftCheck)
      }
    }
  }, [planType, privateOfferWithErrors, eulaFiles])
  return { sendDisabled, draftDisabled }
}

/**
 * On the private offer or resale check if the "installments" are empty and they can have flexible payment terms
 * or if the "offerRecipients" are empty
 * or if the "dimensions" that don't allow additional usage are empty.
 **/
export const checkEmptyRows = (
  installments: Installments[],
  recipients: OfferRecipients[],
  flexiblePaymentTerms: boolean,
  dimensions: Dimension[],
  isDiscount = false
) => {
  return (
    (isEmpty(installments) && (flexiblePaymentTerms as boolean)) ||
    isEmpty(recipients) ||
    (!isDiscount &&
      isEmpty(
        dimensions.filter(dimension => !dimension.isAdditionalUsageAllowed)
      ))
  )
}

export const validateResellers = (
  resellers: ResellerInfo[],
  errors: Record<string, unknown>
) => {
  return resellers.some((reseller, index) => {
    return (
      isEmpty(reseller?.labraPlatformResellerId) ||
      isEmpty(reseller?.resellerAwsAccountId) ||
      !isEmpty(get(errors, `${index}.labraPlatformResellerId`, '')) ||
      !isEmpty(get(errors, `${index}.resellerAwsAccountId`, ''))
    )
  })
}

/**
 * On the private offer or resale check if the "dimensions" values or "installments" values or "offer recipients" values are empty.
 **/
export const isValidOffersArrayField = ({
  key,
  data,
  errors,
  flexiblePaymentTerms,
  planType,
  pricesArray = ['price'] as [keyof PriceTypes],
  isTypeCPPO = false,
  azurePricingType,
}: {
  key: string
  data: Array<Record<string, unknown>>
  errors: Record<string, unknown>
  flexiblePaymentTerms: boolean
  planType: PlanType
  pricesArray?: [keyof PriceTypes]
  isTypeCPPO?: boolean
  azurePricingType?: AzurePricingType
}) => {
  switch (key) {
    case ResaleAuthorizationFields.DIMENSIONS:
    case PrivateOfferFields.DIMENSIONS:
      return validateDimenions({
        dimensions: data as unknown as Dimension[],
        errors: errors[key] as Array<Record<string, unknown>>,
        flexiblePaymentTerms: flexiblePaymentTerms as boolean,
        planType,
        pricesArray,
        isTypeCPPO,
      })
    case ResaleAuthorizationFields.INSTALLMENTSINFO:
    case PrivateOfferFields.INSTALLMENTS:
      return validateInstallments(
        data,
        errors[key] as Array<Record<string, unknown>>,
        flexiblePaymentTerms as boolean
      )
    case ResaleAuthorizationFields.AGREEMENT_RECIPIENTS:
    case PrivateOfferFields.OFFER_RECIPIENTS:
      return validateOfferRecipients(
        data,
        errors[key] as Array<Record<string, unknown>>
      )
    case ResaleAuthorizationFields.RESELLERS_INFO:
      return validateResellers(data, errors[key] as Record<string, unknown>)
    case PrivateOfferFields.PLANS:
      return validatePlans({
        plans: data as unknown as AzureOfferPlan[],
        errors: errors[key] as Array<Record<string, unknown>>,
        azurePricingType,
      })
    default:
      return false
  }
}
/**
 * On the private offer or resale check if the "meta data" values are empty or there are any validation errors on those fields.
 **/
export const validateMetaData = (
  metaData: Array<Record<string, unknown>>,
  errors: Array<Record<string, unknown>>
) => {
  return metaData.some(
    (data, index) =>
      isEmpty(data.key) ||
      isEmpty(data.value) ||
      !isEmpty(get(errors, `${index}.key`, '')) ||
      !isEmpty(get(errors, `${index}.value`, ''))
  )
}

const validateOfferRecipients = (
  offerRecipients: Array<Record<string, unknown>>,
  errors: Array<Record<string, unknown>>
) => {
  return offerRecipients.some(
    (recipient, index) =>
      isEmpty(recipient.firstName) ||
      isEmpty(recipient.lastName) ||
      isEmpty(recipient.email) ||
      !isEmpty(get(errors, `${index}.firstName`, '')) ||
      !isEmpty(get(errors, `${index}.lastName`, '')) ||
      !isEmpty(get(errors, `${index}.email`, ''))
  )
}
/**
 * On the private offer or resale check if flexible payment terms is enabled and all the values on installment rows are filled
 * and there is no validation errors on those fields.
 **/
const validateInstallments = (
  installments: Array<Record<string, unknown>>,
  errors: Array<Record<string, unknown>>,
  flexiblePaymentTerms: boolean
) => {
  if (!flexiblePaymentTerms) {
    return false
  }
  return installments.some((installment, index) => {
    return (
      checkEmptyNumberValues('amount', installment, errors, index) ||
      isEmpty(installment.paymentDate) ||
      !isEmpty((get(errors, `${index}.paymentDate`, '') as string).trim())
    )
  })
}

export const getUpdatedPricesArray = (
  type: string,
  pricesArray: [keyof PriceTypes],
  isTypeCPPO = false
) => {
  return type === 'additional_usage'
    ? [isTypeCPPO ? 'additionalUsagePrice' : 'price']
    : [...pricesArray]
}
export const validateDimensionId = (
  dimension: Dimension,
  errors: Array<Record<string, unknown>>,
  index: number
) => {
  return (
    isEmpty(dimension?.dimensionId) ||
    !isEmpty(get(errors, `${index}.dimensionId`, ''))
  )
}
export const validateCustomDimension = (
  dimension: Dimension,
  errors: Array<Record<string, unknown>>,
  index: number
) => {
  return (
    isEmpty(dimension?.name) ||
    isEmpty(dimension?.label) ||
    !isEmpty(get(errors, `${index}.name`, '')) ||
    !isEmpty(get(errors, `${index}.label`, '')) ||
    isEmpty(dimension?.labelDescription) ||
    !isEmpty(get(errors, `${index}.labelDescription`, ''))
  )
}
/**
 * On the private offer or resale check if dimensions values are filled correctly and no validation errors are there
 * in case of subscription it checks only price and its validations
 * in case of standards when no additional usage dimension is allowed it checks dimension id and price or quantity(price in case when flexible payment term is enabled.)
 * in case of standards when additional usage dimension is allowed it checks dimension id and price.
 * in case of custom it checks name, labelDescription, and price or quantity(price in case when flexible payment term is enabled.)
 **/
export const validateDimenions = ({
  dimensions,
  errors,
  flexiblePaymentTerms,
  planType,
  pricesArray,
  isTypeCPPO = false,
}: {
  dimensions: Dimension[]
  errors: Array<Record<string, unknown>>
  flexiblePaymentTerms: boolean
  planType: PlanType
  pricesArray: [keyof PriceTypes]
  isTypeCPPO?: boolean
}) => {
  const updatedDimensions = dimensions.map(dimension => ({
    ...dimension,
    prices: { ...getDimensionPrice(isTypeCPPO, dimension) },
  }))
  return updatedDimensions.some((dimension, index) => {
    const updatedPricesArrayForData = getUpdatedPricesArray(
      dimension?.type,
      pricesArray,
      isTypeCPPO
    )
    const hasErrorOnPrice = updatedPricesArrayForData.some(price =>
      checkEmptyNumberValues(price, dimension?.prices, errors, index)
    )
    if (
      planType === PlanType.SAAS_SUBSCRIPTION ||
      dimension.type === 'additional_usage'
    ) {
      return hasErrorOnPrice
    } else {
      const hasErrorOnQuantityOrPrice = flexiblePaymentTerms
        ? checkEmptyNumberValues('quantity', dimension, errors, index)
        : hasErrorOnPrice
      if (dimension.type === 'standard') {
        return (
          validateDimensionId(dimension, errors, index) ||
          hasErrorOnQuantityOrPrice
        )
      } else if (dimension.type === 'custom') {
        return (
          validateCustomDimension(dimension, errors, index) ||
          hasErrorOnQuantityOrPrice
        )
      }
    }
  })
}

export const validatePlans = ({
  plans,
  errors,
  azurePricingType,
}: {
  plans: AzureOfferPlan[]
  errors: Array<Record<string, unknown>>
  azurePricingType?: AzurePricingType
}) => {
  return plans.some((planRow, planRowIndex) => {
    const planRowErr = errors[planRowIndex]

    const planWithoutIdErr = isEmpty(planRow.labraPlanId)
    if (planWithoutIdErr) {
      return planWithoutIdErr
    }
    if (azurePricingType === AzurePricingType.LABRA_PERCENTAGE) {
      const planRowPricesErr = planRowErr.prices as Array<
        Record<string, unknown>
      >
      const billingTermErr = planRow.pricing?.recurrentPrice?.prices.some(
        (priceValue, index) =>
          checkEmptyNumberPlansErrValues(
            'pricePerPaymentInUsd',
            priceValue as unknown as Record<string, unknown>,
            planRowPricesErr[index]
          ) ||
          isEmpty(priceValue?.billingTerm?.type) ||
          !isNonEmptyNumber(priceValue?.billingTerm?.value)
      )
      return (
        checkEmptyNumberPlansErrValues(
          'discountPercentage',
          planRow as unknown as Record<string, unknown>,
          planRowErr
        ) || billingTermErr
      )
    } else {
      const planRowPricesErr = planRowErr.prices as Array<
        Record<string, unknown>
      >
      const planRowMetersErr = planRowErr.meters as Record<string, unknown>

      const shouldCheckMetering =
        planRow.pricing?.recurrentPrice?.recurrentPriceMode ===
          RecurrentPriceModeType.FLATRATE &&
        planRow.pricing?.customMeters &&
        Object.keys(planRow.pricing?.customMeters?.meters || {}).length > 0

      const isPerUserPlan =
        planRow.pricing.recurrentPrice.recurrentPriceMode ===
        RecurrentPriceModeType.PERUSER

      const hasErrorOnBilling = planRow.pricing?.recurrentPrice?.prices.some(
        (priceValue, index) =>
          checkEmptyNumberPlansErrValues(
            'pricePerPaymentInUsd',
            priceValue as unknown as Record<string, unknown>,
            planRowPricesErr[index]
          ) ||
          isEmpty(priceValue?.billingTerm?.type) ||
          !isNonEmptyNumber(priceValue?.billingTerm?.value)
      )

      if (shouldCheckMetering) {
        const metersData = planRow.pricing?.customMeters?.meters
        const hasErrornOnMetering =
          metersData &&
          Object.keys(metersData).some(meterKey => {
            const meterErrObj = planRowMetersErr[meterKey] as Record<
              string,
              unknown
            >
            const qunatityErrors = meterErrObj.includedQuantities as Record<
              string,
              unknown
            >[]
            const priceErr = checkEmptyNumberPlansErrValues(
              'pricePerPaymentInUsd',
              metersData[meterKey] as unknown as Record<string, unknown>,
              meterErrObj
            )

            const hasErrorOnQunatity = metersData[
              meterKey
            ].includedQuantities.some(
              (quantityItem, index) =>
                !quantityItem.isInfinite &&
                checkEmptyNumberPlansErrValues(
                  'quantity',
                  quantityItem as unknown as Record<string, unknown>,
                  qunatityErrors[index]
                )
            )
            return priceErr || hasErrorOnQunatity
          })
        return hasErrorOnBilling || hasErrornOnMetering
      } else if (isPerUserPlan) {
        const userLimitsData = {
          ...planRow.pricing.recurrentPrice.userLimits,
        }
        const planRowUserLimitsErr = {
          ...(planRowErr.userLimits as Record<string, unknown>),
        }
        const hasErrorOnUserMinMax =
          (userLimitsData?.minEnable &&
            checkEmptyNumberPlansErrValues(
              'min',
              userLimitsData,
              planRowUserLimitsErr
            )) ||
          (userLimitsData?.maxEnable &&
            checkEmptyNumberPlansErrValues(
              'max',
              userLimitsData,
              planRowUserLimitsErr
            ))
        return hasErrorOnBilling || hasErrorOnUserMinMax
      } else {
        return hasErrorOnBilling
      }
    }
  })
}

export const checkEmptyNumberPlansErrValues = (
  key: string,
  data: Record<string, unknown>,
  error: Record<string, unknown>
) => {
  return (
    data[key] === '' ||
    !isNonEmptyNumber(Number(data[key])) ||
    data[key] === null ||
    !isEmpty((error[key] as string).trim())
  )
}

/**
 * On the private offer or resale check if type is of number it is not null or empty and no validation error is there.
 **/
export const checkEmptyNumberValues = (
  key: string,
  data: Record<string, unknown>,
  errors: Array<Record<string, unknown>>,
  index: number
) => {
  return (
    isNaN(Number(data[key])) ||
    data[key] === null ||
    !isEmpty((get(errors, `${index}[${key}]`, '') as string).trim())
  )
}
